import { ApiHelper } from 'shared/helpers/api';
import { getExploreURL, getUrl, ROUTES } from 'shared/api/urls';
import { IHeadersObject } from 'global';
import { DEFAULT_COUNTRY, DEFAULT_CURRENCY_CODE } from 'shared/constants';
import { getDeviceType } from 'shared/helpers/app';

const VERTICAL_NAME = 'nykaaFashion';

export const getAPlusData = (productId: string) => {
  const baseUrl = getUrl(ROUTES.PRODUCT_SPECIFICATIONS);
  const platform = getDeviceType();

  const params = new URLSearchParams();
  params.append('product_id', String(productId));
  params.append('fields', 'aplus_description');
  params.append('platform', platform);
  const url = `${baseUrl}?${params.toString()}`;

  return ApiHelper(url)
    .then((response: Response) => {
      if (!response.ok) {
        return Promise.reject(response);
      }
      return response.json();
    })
    .catch(() => Promise.reject({ status: 500 }));
};

interface DetailsProps {
  productID: string;
  isNew: boolean;
  headers: IHeadersObject;
  isMobile: boolean;
  isPriceReveal?: boolean;
  isEddV2Enabled?: boolean;
  productSku?: string;
}

export const getProductDetailData = ({
  productID,
  isNew,
  headers,
  isMobile,
  isPriceReveal = false,
  isEddV2Enabled,
  productSku = '',
}: DetailsProps): Promise<any> => {
  const platform = isMobile ? 'MSITE' : 'WEBSITE';

  const searchParams = new URLSearchParams('');

  // appending these to handle Service worker cache to based on currency and country
  searchParams.append('currency', DEFAULT_CURRENCY_CODE);
  searchParams.append('country_code', DEFAULT_COUNTRY);
  searchParams.append('size_data', 'true');

  if (isPriceReveal) searchParams.append('enable_pr', '1');
  if (isEddV2Enabled) searchParams.append('updatedEcomPolicies', '1');
  searchParams.append('platform', platform);

  const url = isNew ? getUrl(ROUTES.PDP_WITH_ID_V3) : getUrl(ROUTES.PDP);

  // If sku is not a valid number, throw 404
  const skuIdNumber = isNew ? Number(productID) : productSku;

  if (!skuIdNumber) {
    return Promise.reject({ status: 404 });
  }

  return ApiHelper(`${url}${skuIdNumber}?${searchParams.toString()}`, { headers }).then(
    async (response: Response) => {
      try {
        const data = await response.json();
        if (response.ok === false) {
          return Promise.reject({ status: response.status, data });
        }
        return data;
      } catch (e) {
        return Promise.reject({ status: 500 });
      }
    }
  );
};

export const getReviewSummary = (productId: string): Promise<any> => {
  const baseUrl = getUrl(ROUTES.PRODUCT_SPECIFICATIONS);
  const platform = getDeviceType();

  // If product id is not a valid number, throw 404
  const isNumber = Number(productId);
  if (!isNumber) return Promise.reject({ status: 404 });

  const params = new URLSearchParams();
  params.append('product_id', String(productId));
  params.append('fields', 'design_code,review_rating_json');
  params.append('platform', platform);
  const url = `${baseUrl}?${params.toString()}`;

  return ApiHelper(`${url}`).then((response: Response) => {
    if (response.ok === false) {
      return Promise.reject(response);
    }
    return response.json();
  });
};

export const getProductExploreImages = (productId: string, imageTagID: string) => {
  const baseUrl = getExploreURL(ROUTES.PDP_EXPLORE).replace('<PRODUCT_ID>', productId);
  const params = new URLSearchParams();
  params.append('compulsory_tags', imageTagID);
  const url = `${baseUrl}?${params.toString()}`;

  return ApiHelper(url, { appendCSRFHeader: false }).then((response: Response) => {
    try {
      const data = response.json();
      if (response.ok === false) {
        return Promise.reject({ status: response.status, data });
      }
      return data;
    } catch (e) {
      return Promise.reject({ status: 500 });
    }
  });
};

interface IGetAllReviewsProps {
  productId: string;
  size: number;
  page: number;
}

export const getAllReviews = ({ productId, size, page }: IGetAllReviewsProps) => {
  const baseUrl = getUrl(ROUTES.ALL_REVIEWS).replace('<PRODUCT_ID>', productId);
  const params = new URLSearchParams();
  params.append('pageNo', String(page));
  params.append('size', String(size));
  const url = `${baseUrl}?${params.toString()}`;

  return ApiHelper(url).then((response: Response) => {
    if (response.ok === false) {
      return Promise.reject(response);
    }
    return response.json();
  });
};

export const getAllImagesForReviews = ({
  productId,
  pageKey,
  size,
}: ReviewAllImages.IGetAllReviewImages) => {
  const baseUrl = getUrl(ROUTES.ALL_IMAGES_FOR_REVIEWS).replace('<PRODUCT_ID>', productId);
  const params = new URLSearchParams();

  params.append('size', String(size));

  if (pageKey) {
    params.append('pageKey', String(pageKey));
  }

  return ApiHelper(`${baseUrl}?${params.toString()}`).then((response: Response) => {
    if (response.ok === false) {
      return Promise.reject(response);
    }
    return response.json();
  });
};

export const likedOrDislikedReview = (productId: string, reviewId: string, action: string) => {
  const baseUrl = getUrl(ROUTES.LIKED_PRODUCT_REVIEW)
    .replace('<PRODUCT_ID>', productId)
    .replace('<REVIEW_ID>', reviewId);

  return ApiHelper(baseUrl, {
    isBodyJSON: true,
    method: 'POST',
    body: { flag: action },
  }).then((response: Response) => {
    if (response.ok === false) {
      const err = {
        status: response.status,
        statusText: response.statusText,
        type: response.type,
        url: response.url,
      };

      return Promise.reject(err);
    }
    return response.json();
  });
};

export const getEcomPolicyData = (productId: number, selectedItem: string) => {
  const baseUrl = getUrl(ROUTES.PRODUCT_SPECIFICATIONS);
  const platform = getDeviceType();
  const params = new URLSearchParams();
  params.append('product_id', String(productId));
  params.append('fields', selectedItem);
  params.append('platform', platform);
  const url = `${baseUrl}?${params.toString()}`;

  return ApiHelper(`${url}`).then((response: Response) => {
    if (!response.ok) {
      return Promise.reject(response);
    }
    return response.json();
  });
};

export const getDeliveryAndShippingInfo = (
  pincode: string,
  SKUs: string[],
  gatewayApiEnabled: boolean
) => {
  const url = getUrl(
    gatewayApiEnabled
      ? ROUTES.GET_DELIVERY_AND_SHIPPING_INFO
      : ROUTES.GET_DELIVERY_AND_SHIPPING_INFO_V2
  );
  const body = {
    ...(pincode !== '' && { pincode: pincode }),
    skus: SKUs,
    domain: VERTICAL_NAME,
  };
  return ApiHelper(url, { method: 'POST', body, isBodyJSON: true }).then((response: Response) => {
    if (!response.ok) {
      return Promise.reject(response);
    }
    return response.json();
  });
};

export const getEstimatedDeliveryDate = ({
  pincode = '',
  SKUs = [],
  source = 'pdp',
  type,
}: {
  pincode: string;
  SKUs: string[];
  source?: string;
  type?: string;
}) => {
  const url = getUrl(ROUTES.GET_ESTIMATED_DELIVERY_INFO);
  const body = {
    ...(pincode !== '' && { pincode: pincode }),
    skus: SKUs,
    domain: 'nykaaFashion',
    source,
    type: type !== '' ? type : undefined,
  };
  return ApiHelper(url, { method: 'POST', body, isBodyJSON: true })
    .then((response: Response) => {
      if (!response.ok) {
        return Promise.reject(response);
      }
      return response.json();
    })
    .catch(() => {
      Promise.reject({ status: 500 });
    });
};
